// @flow

/* eslint-disable ta-code-conventions/has-matching-test */
import * as React from 'react';
import { NavigableList } from '@ta/list.navigable-list';
import { ManagedTextInput } from '@ta/input.text-input';
import { localize } from '@ta/common.i18n';
import POIObjectList from './POIObjectList';
import Typeahead from '../Typeahead';
import type { FetcherResultsMap } from '../Typeahead';
import TypeaheadTextInput from '../TypeaheadTextInput';
import TypeaheadTracking from '../TypeaheadTracking';
import type { TypeaheadResult } from '../TypeaheadResult';
import TypeaheadJsonResult from '../results/TypeaheadJsonResult';
import TypeaheadJsonFetcher, { standardSearchTypes } from '../fetchers/TypeaheadJsonFetcher';
import styles from './MultiLocationSelector.less';

type Props = {|
  selectedLocations: Map<string, TypeaheadResult>,
  selectionChanged: (Map<string, TypeaheadResult>) => void,
  hasResultsChanged: ?(boolean) => void,
  /** max number of locations that can be selected. 0 means unlimited. */
  maxLocations: number,
  /** A CSS class name to apply to the <input/> */
  className?: string,
  /** Callback for when the typeahead input gains focus. */
  onFocus?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Callback for when the typeahead input loses focus. */
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Should selected locations be displayed as POIObject? */
  showLocationsAsPoiObjects: boolean,
|};

type State = {|
  textInputValue: string,
  focused: boolean,
  hasResults: boolean,
|};

/**
 * Component that lets you pick multiple locations with typeahead support.
 */
class MultiLocationSelector extends React.Component<Props, State> {
  static defaultProps = {
    hasResultsChanged: null,
    maxLocations: 0,
    className: undefined,
    onFocus: undefined,
    onBlur: undefined,
    showLocationsAsPoiObjects: true,
  };

  state: State = {
    textInputValue: '',
    focused: false,
    hasResults: false,
  };

  onSelection = (event: SyntheticEvent<HTMLElement> | KeyboardEvent, result: TypeaheadResult) => {
    if (result && result.type === 'location_id' && result.value) {
      const geoId = String(result.value);
      const selectedLocations = new Map(this.props.selectedLocations);

      if (!selectedLocations.has(geoId)) {
        selectedLocations.set(geoId, result);

        // if  maxLocations is exceeded we should remove the first element added
        if (this.props.maxLocations > 0 && selectedLocations.size > this.props.maxLocations) {
          const geoToRemove = Array.from(selectedLocations.keys())[0];
          selectedLocations.delete(geoToRemove);
        }

        this.props.selectionChanged(selectedLocations);
      }
      this.setState({ textInputValue: '' });
      this.updateSearch('');

      if (this.textInput) {
        this.textInput.focus();
      }
    }
  };

  handleResultChange = (results: FetcherResultsMap) => {
    const hasResults = results && results.typeaheadjson && results.typeaheadjson.length > 0;
    if (hasResults && !this.state.hasResults) {
      if (this.props.hasResultsChanged) {
        this.props.hasResultsChanged(true);
      }
      this.setState({ hasResults: true });
    } else if (!hasResults && this.state.hasResults) {
      if (this.props.hasResultsChanged) {
        this.props.hasResultsChanged(false);
      }
      this.setState({ hasResults: false });
    }
  };

  handleItemRemoveClick = (geoId: string) => {
    const selectedLocations = new Map(this.props.selectedLocations);
    selectedLocations.delete(geoId);
    this.props.selectionChanged(selectedLocations);
  };

  typeaheadJsonFetcher = new TypeaheadJsonFetcher({
    interleaved: true,
    types: standardSearchTypes,
    name_depth: 1,
    details: true,
    legacy_format: true,
    urlList: false,
    max: 8,
    scoreThreshold: 0.8,
  });

  tracker = new TypeaheadTracking('', '');
  textInput: ?HTMLInputElement;
  updateSearch: string => void;

  render() {
    return (
      <Typeahead fetchers={[this.typeaheadJsonFetcher]} onResultsChange={this.handleResultChange}>
        {(results, updateSearch) => {
          this.updateSearch = updateSearch;
          return (
            <div
              className={`${styles.wrapper} ${this.props.className ? this.props.className : ''}`}
            >
              <div
                className={`${styles.inputContainer} ${
                  this.state.focused ? styles.inputFocused : ''
                }`}
                onClick={() => this.textInput && this.textInput.focus()}
              >
                <span className={`ui_icon search ${styles.searchIcon}`} />
                <TypeaheadTextInput
                  inputComponentType={ManagedTextInput}
                  inputComponentClassName={styles.textInput}
                  value={this.state.textInputValue}
                  onChange={(newValue: string) => this.setState({ textInputValue: newValue })}
                  onQueryChange={(newValue: string) => {
                    updateSearch(newValue);
                  }}
                  onFocus={e => {
                    this.setState({ focused: true });
                    if (this.props.onFocus) {
                      this.props.onFocus(e);
                    }
                  }}
                  onBlur={e => {
                    this.setState({ focused: false });
                    if (this.props.onBlur) {
                      this.props.onBlur(e);
                    }
                  }}
                  searchDelay={50}
                  placeholder={
                    this.props.selectedLocations.size === 0 ||
                    this.props.selectedLocations.size === this.props.maxLocations
                      ? localize('post_video_typeahead_placeholder_text_search')
                      : localize('post_video_location_typeahead_add_location')
                  }
                  onRef={textInput => {
                    this.textInput = textInput;
                  }}
                />
              </div>
              {results && results.typeaheadjson && results.typeaheadjson.length > 0 ? (
                <div className={styles.results}>
                  <NavigableList>
                    {results && results.typeaheadjson
                      ? results.typeaheadjson.map(r => (isActive, onMouseMove) => (
                          <TypeaheadJsonResult
                            result={r}
                            tracker={this.tracker}
                            onMouseMove={onMouseMove}
                            onSelection={this.onSelection}
                            active={isActive}
                            key={`result=${r.value}`}
                            includeIcon
                          />
                        ))
                      : []}
                  </NavigableList>
                </div>
              ) : null}
              {this.props.showLocationsAsPoiObjects ? (
                <POIObjectList
                  selectedLocations={this.props.selectedLocations}
                  onClose={this.handleItemRemoveClick}
                />
              ) : (
                Array.from(this.props.selectedLocations.entries()).map(([geoId, result]) => (
                  <div
                    className={`ui_tag_box ui_icon times ${styles.selectedItem}`}
                    key={geoId}
                    onClick={() => this.handleItemRemoveClick(geoId)}
                  >
                    {result.displayValue}
                  </div>
                ))
              )}
            </div>
          );
        }}
      </Typeahead>
    );
  }
}

export default MultiLocationSelector;
